import { yupResolver } from "@hookform/resolvers/yup";
import { ProductType } from "@repo/api";
import {
    AMLFormDefaultValues,
    amlIdentificationStateSelector,
    setAMLIdentificationState,
    useAppDispatch,
    useAppSelector,
    type AMLFormValues,
} from "@repo/logic";
import { FC, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTypedTranslation } from "../../hooks";
import { AMLQuestionsForm } from "../AML/AMLQuestionsForm";
import { mapAMLRequest } from "../AML/helpers";
import { AMLQuestionsSchema } from "../AML/schema";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { ChangePensionContributionStepExtraProps } from "./ChangePensionContribution.types";

type AMLIdentificationPageProps = DefaultStepProps & ChangePensionContributionStepExtraProps;

export const AMLIdentificationPage: FC<AMLIdentificationPageProps> = ({
    setContinueCondition: stepperContinueCondition,
    queries: { setDPSAmlIdentificationMutation, setPPAmlIdentificationMutation },
    contractId,
    contractType,
    setModalTitle,
}) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const storedState = useAppSelector(amlIdentificationStateSelector);

    const methods = useForm<AMLFormValues>({
        defaultValues: storedState ?? AMLFormDefaultValues,
        mode: "onChange",
        resolver: yupResolver(AMLQuestionsSchema, undefined, { raw: true }),
        // Prevent RHF from auto focusing the first invalid field (which triggers an instant native scroll)
        // Our custom useScrollFirstError handles smooth scrolling explicitly.
        shouldFocusError: false,
    });

    const { handleSubmit, setError, clearErrors } = methods;

    // Set custom title just for this step
    useEffect(() => {
        setModalTitle?.(t("AML.amlQuestionaire.title"));
        return () => {
            // Reset title back to default on unmount
            setModalTitle?.(null);
        };
    }, [setModalTitle, t]);

    const validateAndSubmit = useCallback(async () => {
        clearErrors("submitError");
        let canContinue = false;

        await handleSubmit(
            // onValid form
            async (data: AMLFormValues) => {
                //Map to request type
                const mapped = mapAMLRequest(data);
                try {
                    const mutation =
                        contractType === ProductType.Pf
                            ? setPPAmlIdentificationMutation
                            : setDPSAmlIdentificationMutation;
                    await mutation?.mutateAsync({
                        contractId,
                        setAmlIdentificationRequest: mapped,
                    });
                    dispatch(setAMLIdentificationState(data));
                    canContinue = true;
                } catch {
                    setError("submitError", { type: "manual", message: t("AML.amlQuestionaire.submissionError") });
                    canContinue = false;
                }
            },
            // onInvalid form
            () => {
                canContinue = false;
            },
        )();

        return canContinue;
    }, [
        clearErrors,
        contractId,
        contractType,
        dispatch,
        handleSubmit,
        setDPSAmlIdentificationMutation,
        setPPAmlIdentificationMutation,
        setError,
        t,
    ]);

    useEffect(() => {
        stepperContinueCondition(validateAndSubmit);
    }, [stepperContinueCondition, validateAndSubmit]);

    return (
        <FormProvider {...methods}>
            <AMLQuestionsForm />
        </FormProvider>
    );
};
